import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useBlocker } from 'react-router-dom';
import type { InviteChannel, OutreachNiche } from '@/api/types';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useApi } from '@/hooks/useApi';
import { isBulkNotAllowed, useBulkApi } from '@/hooks/useBulkImport';
import { creatorTypeLabel } from '@/lib/labels';
import { columnNames, detectColumns, detectHeaderRow, EMPTY_MAPPING, type ColumnMapping, type ColumnRole } from '@/lib/bulk-import/columns';
import { resultsCsv, statusText, type BulkAction, type RowOutcome } from '@/lib/bulk-import/copy';
import { readSpreadsheet, SpreadsheetError, type SheetData } from '@/lib/bulk-import/file';
import { autoMapNiches, distinctNiches, nicheKey } from '@/lib/bulk-import/niches';
import {
  buildRows,
  checkItems,
  dominantChannel,
  hasPlainHandles,
  importNotes,
  rowStatus,
  selectedByDefault,
  type ImportRow,
  type RowCheck,
} from '@/lib/bulk-import/rows';
import { BulkNotAllowedError, runCheck, runLog, runMessaged, type LogTask, type MessagedTask } from '@/lib/bulk-import/run';
import { ActionStep, messagedSkipReason } from './ActionStep';
import { ColumnsStep } from './ColumnsStep';
import { ResultsStep } from './ResultsStep';
import { ReviewStep } from './ReviewStep';
import { StepIndicator, type WizardStep } from './shared';
import { UploadStep } from './UploadStep';

interface WizardProps {
  allowedChannels: InviteChannel[];
  allowedCategories: string[];
  screenshotRequired: boolean;
  /** The server said no (403 BULK_IMPORT_NOT_ALLOWED) mid-run. */
  onNotAllowed: () => void;
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong';
}

export function BulkImportWizard({ allowedChannels, allowedCategories, screenshotRequired, onNotAllowed }: WizardProps): JSX.Element {
  const api = useApi();
  const bulk = useBulkApi();

  const [step, setStep] = useState<WizardStep>('upload');

  // Upload
  const [fileName, setFileName] = useState<string | null>(null);
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);

  // Columns
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState<ColumnMapping>(EMPTY_MAPPING);
  const [fallbackChannel, setFallbackChannel] = useState<InviteChannel | null>(null);

  // Review
  const [checks, setChecks] = useState<Map<number, RowCheck>>(new Map());
  const [checking, setChecking] = useState<{ done: number; total: number } | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [nicheOverrides, setNicheOverrides] = useState<Record<string, string | null>>({});

  // Do it
  const [action, setAction] = useState<BulkAction>('messaged');
  const [screenshots, setScreenshots] = useState<Map<number, File>>(new Map());
  const [category, setCategory] = useState(allowedCategories.length === 1 ? allowedCategories[0]! : '');

  // Results
  const [outcomes, setOutcomes] = useState<Map<number, RowOutcome>>(new Map());
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [actedOn, setActedOn] = useState<number[]>([]);
  const [ranAction, setRanAction] = useState<BulkAction>('messaged');
  const outcomesRef = useRef<Map<number, RowOutcome>>(new Map());
  const stopRef = useRef(false);
  const running = progress !== null || checking !== null;

  useEffect(() => {
    stopRef.current = false;
    return () => {
      // Leaving the page stops starting new requests.
      stopRef.current = true;
    };
  }, []);

  // ── Derived rows ────────────────────────────────────────────────────────

  const sheet = sheets[sheetIndex];
  const allowedKey = allowedChannels.join(',');
  const rows: ImportRow[] = useMemo(
    () =>
      sheet && mapping.handle !== null
        ? buildRows(sheet.rows, { hasHeader, mapping, fallbackChannel, allowedChannels: allowedKey.split(',') as InviteChannel[] })
        : [],
    [sheet, hasHeader, mapping, fallbackChannel, allowedKey],
  );
  const statuses = useMemo(() => rows.map((r) => rowStatus(r, checks.get(r.key))), [rows, checks]);
  const dataRowCount = sheet ? Math.max(0, sheet.rows.length - (hasHeader ? 1 : 0)) : 0;
  const names = useMemo(() => (sheet ? columnNames(sheet.rows, hasHeader) : []), [sheet, hasHeader]);
  const plainHandles = useMemo(
    () => (sheet ? hasPlainHandles(sheet.rows, hasHeader, mapping.handle) : false),
    [sheet, hasHeader, mapping.handle],
  );

  // Niche list: same query (and cache) as the Log reply page.
  const nicheCategory = allowedCategories.length === 1 ? allowedCategories[0]! : '';
  const niches = useQuery({
    queryKey: ['niches', nicheCategory],
    queryFn: () => api.get<OutreachNiche[]>('/portal/niches', nicheCategory ? { category: nicheCategory } : undefined),
    enabled: mapping.niche !== null,
  });
  const nicheOptions = useMemo(
    () => [...(niches.data ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName)).map((n) => ({ code: n.code, displayName: n.displayName })),
    [niches.data],
  );
  const distinct = useMemo(() => (mapping.niche === null ? [] : distinctNiches(rows.map((r) => r.niche))), [rows, mapping.niche]);
  const nicheMap = useMemo(() => ({ ...autoMapNiches(distinct, nicheOptions), ...nicheOverrides }), [distinct, nicheOptions, nicheOverrides]);

  // ── Upload ──────────────────────────────────────────────────────────────

  function applySheet(next: SheetData[], index: number): void {
    const s = next[index];
    if (!s) return;
    const header = detectHeaderRow(s.rows);
    const m = detectColumns(s.rows, header);
    setSheetIndex(index);
    setHasHeader(header);
    setMapping(m);
    const dominant = dominantChannel(s.rows, header, m.handle);
    setFallbackChannel(
      dominant && allowedChannels.includes(dominant) ? dominant : allowedChannels.length === 1 ? allowedChannels[0]! : null,
    );
    resetFrom('columns');
  }

  async function onFile(file: File): Promise<void> {
    setReading(true);
    setReadError(null);
    try {
      const parsed = await readSpreadsheet(file);
      setFileName(file.name);
      setSheets(parsed);
      // Start on the first sheet that has data.
      const first = Math.max(0, parsed.findIndex((s) => s.rows.length > 1));
      applySheet(parsed, first);
    } catch (err) {
      setFileName(null);
      setSheets([]);
      setReadError(err instanceof SpreadsheetError ? err.message : `Couldn't read that file: ${errorMessage(err)}`);
    } finally {
      setReading(false);
    }
  }

  /** Forget everything downstream of a step whose inputs changed. */
  function resetFrom(from: 'columns' | 'review'): void {
    setChecks(new Map());
    setCheckError(null);
    setSelected(new Set());
    setScreenshots(new Map());
    if (from === 'columns') {
      // Row keys change with the columns, so earlier results no longer line up.
      setNicheOverrides({});
      outcomesRef.current = new Map();
      setOutcomes(new Map());
      setActedOn([]);
    }
  }

  // ── Columns → check ─────────────────────────────────────────────────────

  function onMapping(role: ColumnRole, col: number | null): void {
    setMapping((prev) => {
      const next = { ...prev };
      // One column, one role: picking a column takes it away from another role.
      if (col !== null) {
        for (const r of Object.keys(next) as ColumnRole[]) if (next[r] === col) next[r] = null;
      }
      next[role] = col;
      return next;
    });
    resetFrom('columns');
  }

  const startCheck = useCallback(async (): Promise<void> => {
    const items = checkItems(rows);
    setStep('review');
    setCheckError(null);
    setChecking({ done: 0, total: items.length });
    try {
      const result = await runCheck(bulk, items, {
        isNotAllowed: isBulkNotAllowed,
        shouldStop: () => stopRef.current,
        onProgress: (done, total) => setChecking({ done, total }),
      });
      if (stopRef.current) return;
      setChecks(result);
      setSelected(new Set(rows.filter((r) => selectedByDefault(rowStatus(r, result.get(r.key)))).map((r) => r.key)));
    } catch (err) {
      if (err instanceof BulkNotAllowedError) {
        onNotAllowed();
        return;
      }
      setCheckError(errorMessage(err));
    } finally {
      setChecking(null);
    }
  }, [rows, bulk, onNotAllowed]);

  // ── Run ─────────────────────────────────────────────────────────────────

  const rowByKey = useMemo(() => new Map(rows.map((r) => [r.key, r])), [rows]);
  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.key)), [rows, selected]);

  async function run(which: BulkAction, keys: number[]): Promise<void> {
    const targets = keys.map((k) => rowByKey.get(k)).filter((r): r is ImportRow => Boolean(r));
    setRanAction(which);
    setStep('results');
    // Keep earlier outcomes (a retry only replaces the rows it re-sends).
    const map = new Map(outcomesRef.current);
    for (const r of targets) map.delete(r.key);
    outcomesRef.current = map;
    setOutcomes(new Map(map));
    setActedOn((prev) => [...new Set([...prev, ...targets.map((r) => r.key)])].sort((a, b) => a - b));

    const record = (key: number, o: RowOutcome): void => {
      outcomesRef.current.set(key, o);
    };
    const flush = (done: number, total: number): void => {
      setOutcomes(new Map(outcomesRef.current));
      setProgress({ done, total });
    };
    const ctl = { isNotAllowed: isBulkNotAllowed, shouldStop: () => stopRef.current, onProgress: flush };
    const fileLabel = fileName ?? 'file';

    try {
      if (which === 'messaged') {
        const tasks: MessagedTask[] = [];
        for (const r of targets) {
          const skip = messagedSkipReason(statuses[r.key]!);
          if (skip) record(r.key, { ok: true, skipped: true, message: skip });
          else tasks.push({ key: r.key, channel: r.channel!, targetIdentifier: r.username!, note: importNotes(r, fileLabel) });
        }
        flush(0, tasks.length);
        await runMessaged(bulk, tasks, record, ctl);
      } else {
        const tasks: LogTask[] = targets.map((r) => {
          const nicheCode = r.niche ? nicheMap[nicheKey(r.niche)] ?? null : null;
          return {
            key: r.key,
            item: {
              channel: r.channel!,
              targetIdentifier: r.username!,
              ...(category ? { category } : {}),
              ...(nicheCode ? { niches: [nicheCode] } : {}),
              ...(r.email ? { email: r.email } : {}),
              notes: importNotes(r, fileLabel),
            },
            screenshot: screenshots.get(r.key) ?? null,
          };
        });
        flush(0, tasks.length);
        await runLog(bulk, tasks, record, ctl);
      }
    } catch (err) {
      if (err instanceof BulkNotAllowedError) {
        onNotAllowed();
        return;
      }
      for (const r of targets) if (!outcomesRef.current.has(r.key)) record(r.key, { ok: false, message: errorMessage(err) });
    } finally {
      setOutcomes(new Map(outcomesRef.current));
      setProgress(null);
      bulk.invalidate();
    }
  }

  function retryFailed(): void {
    const failed = actedOn.filter((k) => {
      const o = outcomesRef.current.get(k);
      return o && !o.ok;
    });
    void run(ranAction, failed);
  }

  function download(): void {
    const acted = new Set(actedOn);
    const csv = resultsCsv(
      rows,
      (r) => statusText(statuses[r.key]!, checks.get(r.key), { error: r.error, rowNumber: r.rowNumber }),
      new Map([...outcomesRef.current].filter(([k]) => acted.has(k))),
      ranAction,
      window.location.origin,
    );
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(fileName ?? 'import').replace(/\.[^.]+$/, '')}-results.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /** Back to Do it with only the failed rows ticked, to add screenshots or change the action. */
  function fixFailed(): void {
    const failed = actedOn.filter((k) => {
      const o = outcomesRef.current.get(k);
      return o && !o.ok;
    });
    setSelected(new Set(failed));
    setAction(ranAction);
    setStep('action');
  }

  function startOver(): void {
    setStep('upload');
    setFileName(null);
    setSheets([]);
    setSheetIndex(0);
    setReadError(null);
    setMapping(EMPTY_MAPPING);
    resetFrom('columns');
  }

  // ── Leaving mid-run ─────────────────────────────────────────────────────

  const blocker = useBlocker(({ currentLocation, nextLocation }) => running && currentLocation.pathname !== nextLocation.pathname);
  const leavingRef = useRef(false);
  useEffect(() => {
    if (!running) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [running]);

  // A new step starts at its top, not wherever the last one was scrolled to.
  const topRef = useRef<HTMLDivElement>(null);
  const shownStep = useRef(step);
  useEffect(() => {
    if (shownStep.current === step) return;
    shownStep.current = step;
    topRef.current?.scrollIntoView({ block: 'start' });
  }, [step]);

  // ── Render ──────────────────────────────────────────────────────────────

  const canPickStep = !running && step !== 'results';
  const loggedWithoutScreenshot = actedOn.filter((k) => outcomes.get(k)?.ok && !outcomes.get(k)?.skipped && !screenshots.has(k)).length;

  return (
    <div ref={topRef} className="scroll-mt-24 space-y-6">
      <StepIndicator current={step} onPick={canPickStep ? setStep : undefined} />

      {step === 'upload' && (
        <UploadStep
          fileName={fileName}
          sheets={sheets}
          sheetIndex={sheetIndex}
          dataRows={dataRowCount}
          loading={reading}
          error={readError}
          onFile={(f) => void onFile(f)}
          onSheet={(i) => applySheet(sheets, i)}
          onNext={() => setStep('columns')}
        />
      )}

      {step === 'columns' && sheet && (
        <ColumnsStep
          columns={names}
          mapping={mapping}
          onMapping={onMapping}
          hasHeader={hasHeader}
          onHasHeader={(v) => {
            setHasHeader(v);
            resetFrom('columns');
          }}
          fallbackChannel={fallbackChannel}
          onFallbackChannel={(c) => {
            setFallbackChannel(c);
            resetFrom('columns');
          }}
          allowedChannels={allowedChannels}
          hasPlainHandles={plainHandles}
          rows={rows}
          sample={sheet.rows.slice(hasHeader ? 1 : 0, (hasHeader ? 1 : 0) + 5)}
          onBack={() => setStep('upload')}
          onNext={() => void startCheck()}
        />
      )}

      {step === 'review' && (
        <ReviewStep
          rows={rows}
          statuses={statuses}
          checks={checks}
          checking={checking}
          checkError={checkError}
          onRetryCheck={() => void startCheck()}
          selected={selected}
          onSelect={(keys, on) =>
            setSelected((prev) => {
              const next = new Set(prev);
              for (const k of keys) {
                if (on) next.add(k);
                else next.delete(k);
              }
              return next;
            })
          }
          distinct={distinct}
          nicheOptions={nicheOptions}
          nichesLoading={niches.isLoading}
          nicheMap={nicheMap}
          onNicheMap={(key, code) => setNicheOverrides((prev) => ({ ...prev, [key]: code }))}
          onBack={() => setStep('columns')}
          onNext={() => setStep('action')}
        />
      )}

      {step === 'action' && (
        <ActionStep
          rows={selectedRows}
          statusOf={(r) => statuses[r.key]!}
          action={action}
          onAction={setAction}
          screenshotRequired={screenshotRequired}
          screenshots={screenshots}
          onScreenshots={setScreenshots}
          categories={allowedCategories.map((c) => ({ value: c, label: creatorTypeLabel(c) }))}
          category={category}
          onCategory={setCategory}
          onUntick={(keys) =>
            setSelected((prev) => {
              const next = new Set(prev);
              for (const k of keys) next.delete(k);
              return next;
            })
          }
          onBack={() => setStep('review')}
          onRun={() => void run(action, selectedRows.map((r) => r.key))}
        />
      )}

      {step === 'results' && (
        <ResultsStep
          action={ranAction}
          rows={actedOn.map((k) => rowByKey.get(k)).filter((r): r is ImportRow => Boolean(r))}
          outcomes={outcomes}
          progress={progress}
          loggedWithoutScreenshot={ranAction === 'log' ? loggedWithoutScreenshot : 0}
          onRetryFailed={retryFailed}
          onFixFailed={fixFailed}
          onDownload={download}
          onStartOver={startOver}
        />
      )}

      <ConfirmDialog
        open={blocker.state === 'blocked'}
        onOpenChange={(open) => {
          if (!open && !leavingRef.current && blocker.state === 'blocked') blocker.reset();
        }}
        tone="destructive"
        title="Leave while the import is running?"
        description="Rows already sent stay done. Everything not sent yet is dropped, and you'd need to import those again."
        confirmLabel="Leave"
        cancelLabel="Stay"
        onConfirm={() => {
          leavingRef.current = true;
          stopRef.current = true;
          if (blocker.state === 'blocked') blocker.proceed();
        }}
      />
    </div>
  );
}
